import { Linear, Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import { CaptionToken } from "../Data/CaptionToken";
import * as Constants from "../Data/Constants";
import { GameType } from "../Data/GameData";
import { GameProgress } from "../Data/GameProgress";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { Andy } from "../GameObjects/Andy";
import { Button } from "../GameObjects/Button";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { OnboardingManager } from "../GameObjects/OnboardingManager";
import { Trailer } from "../GameObjects/Trailer";

export abstract class Game extends HLF.State {
    // override in child class
    protected readonly _gameType: GameType = -1;

    protected _overlay: PIXI.Sprite;
    protected _andy: Andy;
    protected _buttonClear: Button;
    protected _buttonSubmit: Button;
    protected _keyboardFocusManager: KeyboardFocusManager;
    protected _onboardingManager: OnboardingManager;
    protected _trailer: Trailer;
    protected _isFirstGame!: boolean;
    protected _isFirstVisit!: boolean;
    protected _isOnboarding!: boolean;
    protected _pubSubTokens: any[];
    protected _activeFunFactVo: string | undefined;
    protected _funFactCompleteTimeline: TimelineLite | undefined;
    protected _funFactCommon!: string[];
    protected _funFactUnique!: string[][];
    protected _currentFunFactIndex: number;

    constructor() {
        super();

        this._pubSubTokens = new Array<any>();
        this._currentFunFactIndex = 0;

        this._trailer = new Trailer();
        this.addChild(this._trailer);

        this._andy = new Andy();
        this.addChild(this._andy);

        this._buttonClear = new Button(
            this.onClearPressed.bind(this),
            "button-clear", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_CLICK,
        );
        this.addChild(this._buttonClear);

        this._buttonSubmit = new Button(
            this.onSubmitPressed.bind(this),
            "button-submit", Constants.SPRITESHEET_UI,
            undefined,
            SoundEffect.UI_SELECT,
        );
        this.addChild(this._buttonSubmit);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);

        this._onboardingManager = new OnboardingManager();
        this.addChild(this._onboardingManager);

        this._overlay = HLF.PixiUtils.makeSprite("black2x2", Constants.SPRITESHEET_UI);
        this._overlay.interactive = true;
        this._overlay.visible = false;
        this.addChild(this._overlay);

        this.scale.set(1.1);
        const timeline = new TimelineLite();
        timeline.to(this.scale, 1.0, {
            x: 1,
            y: 1,
            ease: Linear.easeNone,
        }, 0);

        this._pubSubTokens.push(PubSub.subscribe(Topics.PLAY_FUN_FACT, this.playFunFact.bind(this)));

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "studio-wall-centre-frame0000.png",
            "studio-wall-side-frame0000.png",
            "trailer-exterior.jpg",
            "door-sequence.json",
        ];
        return requiredAssets;
    }

    public get progress(): GameProgress {
        return AppProgress.games[this._gameType];
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);

        this._isFirstVisit = AppProgress.games[this._gameType].isFirstVisit;
        AppProgress.games[this._gameType].isFirstVisit = false;

        this._isOnboarding = (
            AppProgress.games[this._gameType].isOnboardingComplete === false &&
            Constants.DEBUG_SKIP_ONBOARDING === false
        );
        this._isFirstGame = this._isOnboarding && this._gameType === GameType.MASK;
        AppProgress.games[this._gameType].isOnboardingComplete = true;
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        for (let i = 0; i < this._pubSubTokens.length; ++i) {
            PubSub.unsubscribe(this._pubSubTokens[i]);
        }
        delete this._funFactCompleteTimeline;
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
        this._andy.position.copy(this.getAndyPos());
        if (this._buttonSubmit.interactive) {
            this._buttonSubmit.position.copy(this.getSubmitButtonPos());
        }
        else {
            this._buttonSubmit.position.x = this._size!.x * 0.5 + this._buttonSubmit.width;
        }
        this._onboardingManager.onResize();
        this._overlay.scale.set(this._size.x, this._size.y);
    }

    public update(pDeltaTime: number) {
        if (this._andy !== undefined) {
            this._andy.update(pDeltaTime);
            this._trailer.update(pDeltaTime);
        }
    }

    public animateIn(pOnComplete: () => void): void {
        this._andy.position.x = this._size.x * 0.5 + this._andy.width;

        const timeline: TimelineLite = new TimelineLite();
        if (this._isFirstGame === false) {
            this._overlay.alpha = 1;
            this._overlay.visible = true;
            HLF.PixiUtils.sendToFront(this._overlay);
            timeline.to(this._overlay, 1.0, {
                alpha: 0,
                ease: Sine.easeInOut,
                onComplete: () => { this._overlay.visible = false; },
            }, 0.5);
        }
        timeline.to(this._andy.position, 0.5, {
            x: this.getAndyPos().x,
            ease: Sine.easeOut,
        }, 0.5);
        timeline.call(pOnComplete);
    }

    public animateOut(pOnComplete: () => void): void {
        this.stopActiveFunFact();
        this._andy.cancelIdleTimer();
        this._overlay.visible = true;
        this._overlay.alpha = 0;

        const timeline: TimelineLite = new TimelineLite();

        timeline.to(this._andy.position, 0.5, {
            x: this._size.x * 0.5 + this._andy.width,
            ease: Sine.easeIn,
        }, 0.5);

        if (this._isFirstGame) {
            timeline.add(this._trailer.playWallAnimation(), 0.5);
            timeline.call(this._trailer.playDoorAnim, undefined, this._trailer);
            timeline.to(this.scale, 1.5, {
                x: this.scale.x * 1.4,
                y: this.scale.y * 1.4,
                ease: Linear.easeNone,
            }, 2.0);
        }
        else {
            timeline.to(this.scale, 1.5, {
                x: 1.1,
                y: 1.1,
                ease: Linear.easeNone,
            }, 0.5);
        }
        timeline.to(this._overlay, 1.0, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, timeline.duration() - 1.0);
        timeline.call(pOnComplete);
    }

    protected abstract saveData(): void;

    protected onAnimateInComplete(): void {
        PubSub.publishSync(Topics.GAME_ENTERED, undefined);
    }

    protected onSubmitPressed(): void {
        AppProgress.recentGame = this._gameType;
        this.saveData();

        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
            Constants.STATE_MENU,
            Constants.LOAD_INTERSTITIAL,
        ));

        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        Application.instance.analytics.event("activity", "complete", GameType[this._gameType]);
    }

    protected onClearPressed(): void {
        // override
    }

    protected getAndyPos(): PIXI.Point {
        return new PIXI.Point(
            Math.max(this._size.x * 0.5 - this._andy.width * 0.5 - 75, 400),
            this._size.y * 0.5 + this._andy.height * 0.3,
        );
    }

    protected getSubmitButtonPos(): PIXI.Point {
        return new PIXI.Point(
            this._size.x * 0.5 - 80,
            0,
        );
    }

    protected playFunFact(cb?: () => void): void {
        this._activeFunFactVo = this.getFunFact();

        PubSub.publishSync(Topics.FUN_FACT_START, undefined);
        Application.instance.audio.preload(this._activeFunFactVo, HLF.AudioCategory.VO, () => {
            const dur: number = Application.instance.audio.getDuration(this._activeFunFactVo!, HLF.AudioCategory.VO)!;
            this._funFactCompleteTimeline = new TimelineLite();
            this._funFactCompleteTimeline.call(() => {
                if (typeof cb === "function") {
                    cb();
                }
                PubSub.publishSync(Topics.FUN_FACT_COMPLETE, undefined);
            }, undefined, this, dur);
            PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(this._activeFunFactVo!));
        });

        PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
            this._activeFunFactVo,
            1, false, HLF.AudioCategory.VO,
        ));
    }

    protected stopActiveFunFact(): boolean {
        if (this._funFactCompleteTimeline !== undefined) {
            this._funFactCompleteTimeline.kill();
            this._funFactCompleteTimeline.clear();
        }
        if (this._activeFunFactVo !== undefined) {
            Application.instance.audio.fadeOut(this._activeFunFactVo, HLF.AudioCategory.VO);
            PubSub.publishSync(Topics.STOP_CAPTION, undefined);
            return true;
        }
        return false;
    }

    protected getFunFact(): string {
        let funFact: string;
        const nextUnique: number = this.progress.funFactUniqueIndex[this._currentFunFactIndex];
        const nextCommon: number = this.progress.funFactCommonIndex;
        // Always play the first common fun fact upon first visiting a game
        if (this._isFirstVisit) {
            this._isFirstVisit = false;
            funFact = this._funFactCommon[0];
            this.progress.funFactCommonIndex++;
        }
        // Otherwise, prioritize unique fun facts
        else if (nextUnique < this._funFactUnique[this._currentFunFactIndex].length) {
            funFact = this._funFactUnique[this._currentFunFactIndex][nextUnique];
            this.progress.funFactUniqueIndex[this._currentFunFactIndex]++;
        }
        // If all unique fun facts have played, use common fun facts
        else if (nextCommon < this._funFactCommon.length) {
            funFact = this._funFactCommon[nextCommon];
            this.progress.funFactCommonIndex++;
        }
        // If all of common and unique have played, reset progress and start from the beginning
        else {
            this.progress.funFactUniqueIndex[this._currentFunFactIndex] = 0;
            this.progress.funFactCommonIndex = 0;
            funFact = this.getFunFact();
        }
        return funFact;
    }
}
